import { onSnapshot, runTransaction, serverTimestamp, setDoc } from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import { leagueSettingsRef, requireLeagueOrgId } from "./leagueRepository";

export const MAX_GALLERY_IMAGES = 10;
export const MAX_GALLERY_FILE_BYTES = 15 * 1024 * 1024;
export const GALLERY_UPLOAD_CONCURRENCY = 3;
export const GALLERY_SLIDESHOW_INTERVALS = [3000, 5000, 10000];
export const DEFAULT_GALLERY_SLIDESHOW_INTERVAL = 5000;

function sortGallery(gallery = []) {
  return [...gallery].sort((a, b) => {
    const aOrder = Number.isFinite(Number(a?.order)) ? Number(a.order) : 9999;
    const bOrder = Number.isFinite(Number(b?.order)) ? Number(b.order) : 9999;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return String(a?.uploadedAt || "").localeCompare(String(b?.uploadedAt || ""));
  });
}

export function subscribeLeagueGallery(orgId, onValue, onError) {
  if (!orgId) return undefined;
  return onSnapshot(
    leagueSettingsRef(orgId),
    (snapshot) => {
      const data = snapshot.data() || {};
      const interval = GALLERY_SLIDESHOW_INTERVALS.includes(Number(data.gallerySlideshowIntervalMs))
        ? Number(data.gallerySlideshowIntervalMs)
        : DEFAULT_GALLERY_SLIDESHOW_INTERVAL;
      onValue(sortGallery(Array.isArray(data.gallery) ? data.gallery : []), { slideshowIntervalMs: interval });
    },
    onError,
  );
}

function validateGalleryFile(file) {
  if (!String(file?.type || "").startsWith("image/")) throw new Error("Only image files can be uploaded.");
  if (file.size > MAX_GALLERY_FILE_BYTES) throw new Error(`${file.name || "An image"} must be smaller than 15 MB.`);
}

function safeExtension(file) {
  const fromName = String(file?.name || "").match(/\.([a-zA-Z0-9]+)$/)?.[1];
  if (fromName) return fromName.toLowerCase().slice(0, 8);
  const subtype = String(file?.type || "image/jpeg").split("/")[1] || "jpg";
  return subtype.replace(/[^a-zA-Z0-9]/g, "").slice(0, 8) || "jpg";
}

function uploadFile(storageRef, file, metadata, onProgress) {
  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file, metadata);
    task.on(
      "state_changed",
      (snapshot) => onProgress?.(snapshot.totalBytes ? snapshot.bytesTransferred / snapshot.totalBytes : 0),
      reject,
      async () => {
        try { resolve(await getDownloadURL(task.snapshot.ref)); } catch (error) { reject(error); }
      },
    );
  });
}

async function runWithConcurrency(items, concurrency, worker) {
  const results = new Array(items.length);
  let nextIndex = 0;
  const runners = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (nextIndex < items.length) {
      const index = nextIndex++;
      results[index] = await worker(items[index], index);
    }
  });
  await Promise.all(runners);
  return results;
}

function createUploadDescriptor(file, index, currentCount, orgId) {
  const stamp = Date.now();
  const token = Math.random().toString(36).slice(2, 10);
  const id = `gallery-${stamp}-${token}-${index}`;
  const extension = safeExtension(file);
  const originalPath = `orgs/${orgId}/league/gallery/originals/${id}.${extension}`;
  return {
    file,
    image: {
      id,
      caption: "",
      uploadedAt: new Date(stamp + index).toISOString(),
      originalFileName: file.name || "league-image",
      originalPath,
      storagePath: originalPath,
      processingStatus: "queued",
      imagePipelineVersion: 2,
      order: currentCount + index,
    },
  };
}

export async function uploadLeagueGalleryImages(orgId, files, currentGallery = [], caption = "", onProgress) {
  const safeOrgId = requireLeagueOrgId(orgId);
  const selectedFiles = Array.from(files || []);
  if (!selectedFiles.length) throw new Error("Choose at least one image to upload.");
  if (selectedFiles.length > MAX_GALLERY_IMAGES) throw new Error(`Select no more than ${MAX_GALLERY_IMAGES} images at once.`);
  if (currentGallery.length + selectedFiles.length > MAX_GALLERY_IMAGES) {
    throw new Error(`A League gallery can contain up to ${MAX_GALLERY_IMAGES} images. You can add ${Math.max(0, MAX_GALLERY_IMAGES - currentGallery.length)} more.`);
  }
  selectedFiles.forEach(validateGalleryFile);

  const descriptors = selectedFiles.map((file, index) => {
    const descriptor = createUploadDescriptor(file, index, currentGallery.length, safeOrgId);
    descriptor.image.caption = String(caption || "").trim();
    return descriptor;
  });
  const settingsRef = leagueSettingsRef(safeOrgId);

  // Create placeholders first so the Storage trigger can always find its Firestore item.
  await runTransaction(settingsRef.firestore, async (transaction) => {
    const snapshot = await transaction.get(settingsRef);
    const serverGallery = Array.isArray(snapshot.data()?.gallery) ? snapshot.data().gallery : currentGallery;
    if (serverGallery.length + descriptors.length > MAX_GALLERY_IMAGES) throw new Error("The gallery changed while uploading. Remove photos or try again.");
    const gallery = sortGallery([...serverGallery, ...descriptors.map(({ image }) => image)])
      .map((item, index) => ({ ...item, order: index }));
    transaction.set(settingsRef, { gallery, galleryUpdatedAt: serverTimestamp() }, { merge: true });
  });

  const itemProgress = descriptors.map(({ file }) => ({ name: file.name, status: "waiting", progress: 0 }));
  let progressFrame = null;
  const flushProgress = () => {
    progressFrame = null;
    const totalProgress = itemProgress.reduce((sum, item) => sum + item.progress, 0);
    onProgress?.({
      total: itemProgress.length,
      completed: itemProgress.filter((item) => item.status === "processing" || item.status === "complete").length,
      failed: itemProgress.filter((item) => item.status === "failed").length,
      overall: Math.round((totalProgress / Math.max(1, itemProgress.length)) * 100),
      items: itemProgress.map((item) => ({ ...item })),
    });
  };
  const emitProgress = (immediate = false) => {
    if (immediate) { if (progressFrame && typeof cancelAnimationFrame === "function") cancelAnimationFrame(progressFrame); flushProgress(); return; }
    if (progressFrame) return;
    if (typeof requestAnimationFrame === "function") progressFrame = requestAnimationFrame(flushProgress);
    else progressFrame = setTimeout(flushProgress, 50);
  };
  emitProgress(true);

  const storage = getStorage();
  const results = await runWithConcurrency(descriptors, GALLERY_UPLOAD_CONCURRENCY, async ({ file, image }, index) => {
    try {
      itemProgress[index] = { ...itemProgress[index], status: "uploading", progress: 0.02 };
      emitProgress();
      const originalRef = ref(storage, image.originalPath);
      const originalUrl = await uploadFile(originalRef, file, {
        contentType: file.type || "image/jpeg",
        cacheControl: "public,max-age=31536000,immutable",
        customMetadata: { orgId: safeOrgId, galleryImageId: image.id, pipelineVersion: "2" },
      }, (fraction) => {
        itemProgress[index] = { ...itemProgress[index], status: "uploading", progress: 0.02 + fraction * 0.93 };
        emitProgress();
      });
      itemProgress[index] = { ...itemProgress[index], status: "processing", progress: 1 };
      emitProgress();
      return { ok: true, id: image.id, originalUrl };
    } catch (error) {
      itemProgress[index] = { ...itemProgress[index], status: "failed", progress: 1, error: error?.message || "Upload failed" };
      emitProgress();
      return { ok: false, id: image.id, path: image.originalPath, file, error };
    }
  });

  const successful = results.filter((result) => result.ok);
  const failures = results.filter((result) => !result.ok);

  const gallery = await runTransaction(settingsRef.firestore, async (transaction) => {
    const snapshot = await transaction.get(settingsRef);
    const serverGallery = Array.isArray(snapshot.data()?.gallery) ? snapshot.data().gallery : [];
    const successMap = new Map(successful.map((item) => [item.id, item]));
    const failedIds = new Set(failures.map((item) => item.id));
    const next = sortGallery(serverGallery.filter((item) => !failedIds.has(item.id)).map((item) => {
      const success = successMap.get(item.id);
      if (!success) return item;
      // Preserve derivatives if the Cloud Function has already completed.
      return {
        ...item,
        originalUrl: success.originalUrl,
        url: item.heroUrl || item.url || success.originalUrl,
        processingStatus: item.processingStatus === "ready" ? "ready" : "processing",
      };
    })).map((item, index) => ({ ...item, order: index }));
    transaction.set(settingsRef, {
      gallery: next,
      galleryUpdatedAt: serverTimestamp(),
      galleryLastUploadCount: successful.length,
    }, { merge: true });
    return next;
  });

  await Promise.all(failures.map(async ({ path }) => {
    try { await deleteObject(ref(storage, path)); } catch (_) { /* best effort */ }
  }));
  emitProgress(true);

  if (!successful.length) throw failures[0]?.error || new Error("Unable to upload the selected images.");
  return {
    gallery,
    uploadedCount: successful.length,
    failedCount: failures.length,
    failures: failures.map(({ file, error }) => ({ name: file?.name || "image", message: error?.message || "Upload failed" })),
  };
}

export async function uploadLeagueGalleryImage(orgId, file, currentGallery = [], caption = "", onProgress) {
  const result = await uploadLeagueGalleryImages(orgId, file ? [file] : [], currentGallery, caption, onProgress);
  return result.gallery;
}

export async function updateLeagueGalleryCaption(orgId, imageId, caption, currentGallery = []) {
  const safeOrgId = requireLeagueOrgId(orgId);
  const gallery = sortGallery(currentGallery).map((item) => item.id === imageId ? { ...item, caption: String(caption || "").trim() } : item);
  await setDoc(leagueSettingsRef(safeOrgId), { gallery, galleryUpdatedAt: serverTimestamp() }, { merge: true });
  return gallery;
}

export async function updateLeagueGalleryOrder(orgId, orderedGallery = []) {
  const safeOrgId = requireLeagueOrgId(orgId);
  const gallery = orderedGallery.map((item, index) => ({ ...item, order: index }));
  await setDoc(leagueSettingsRef(safeOrgId), { gallery, galleryUpdatedAt: serverTimestamp() }, { merge: true });
  return gallery;
}

function imageStoragePaths(image) {
  return [...new Set([
    image?.originalPath,
    image?.storagePath,
    image?.thumbnailPath,
    image?.mediumPath,
    image?.heroPath,
  ].filter(Boolean))];
}

async function deleteImageFiles(image) {
  await Promise.all(imageStoragePaths(image).map(async (storagePath) => {
    try { await deleteObject(ref(getStorage(), storagePath)); } catch (error) {
      if (error?.code !== "storage/object-not-found") throw error;
    }
  }));
}

export async function removeLeagueGalleryImage(orgId, image, currentGallery = []) {
  const safeOrgId = requireLeagueOrgId(orgId);
  const gallery = sortGallery(currentGallery.filter((item) => item.id !== image?.id)).map((item, index) => ({ ...item, order: index }));
  await deleteImageFiles(image);
  await setDoc(leagueSettingsRef(safeOrgId), { gallery, galleryUpdatedAt: serverTimestamp() }, { merge: true });
  return gallery;
}

export async function updateLeagueGallerySlideshowInterval(orgId, intervalMs) {
  const safeOrgId = requireLeagueOrgId(orgId);
  const value = Number(intervalMs);
  if (!GALLERY_SLIDESHOW_INTERVALS.includes(value)) throw new Error("Choose a slideshow timer of 3, 5 or 10 seconds.");
  await setDoc(leagueSettingsRef(safeOrgId), { gallerySlideshowIntervalMs: value, galleryUpdatedAt: serverTimestamp() }, { merge: true });
  return value;
}

export async function removeLeagueGalleryImages(orgId, images = [], currentGallery = []) {
  const safeOrgId = requireLeagueOrgId(orgId);
  const selected = Array.from(images || []).filter(Boolean);
  if (!selected.length) throw new Error("Select at least one image to remove.");
  const selectedIds = new Set(selected.map((image) => image.id));
  const gallery = sortGallery(currentGallery.filter((item) => !selectedIds.has(item.id)))
    .map((item, index) => ({ ...item, order: index }));
  await Promise.all(selected.map(deleteImageFiles));
  await setDoc(leagueSettingsRef(safeOrgId), { gallery, galleryUpdatedAt: serverTimestamp() }, { merge: true });
  return gallery;
}
